import { BehaviorSubject, Observable, OperatorFunction, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { QuestionList } from 'src/app/shared/models/questionList';
import { environment } from 'src/environments/environment';
import { Faqs, FaqsMain } from 'src/app/shared/models/category';
import { QuestionItem, Question } from 'src/app/shared/models/questionItem';
import { FaqsSubcategory } from 'src/app/shared/models/subCategory';
import { HeroSupport } from 'src/app/shared/models/heroSupport';
import { map } from 'rxjs/operators';
import { PUBLIC_PRIVATE_MODEL } from 'src/app/shared/constants/loggedinUser';
import { NX23NavbarFields } from 'src/app/shared/types/content-types';
import TokenValidator from '@nx/web-token-validator';

@Injectable({
  providedIn: 'root',
})
export class FaqsService {
  isAuthenticated!: boolean | null;

  constructor(
    private http: HttpClient,
    private tokenValidator: TokenValidator,
  ) {
    this.isAuthenticated = this.tokenValidator.getIsAuthenticated();
  }

  private mainFaqsID = '3ka301n6krmZZ9vWJrWk2D';
  private footerContentfulID = '5HHFqYSV4ImREs8ZywzO8c';
  public _showTitleSearchComponent = new BehaviorSubject<boolean>(true);
  public _showTitleSearchComponent$ =
    this._showTitleSearchComponent.asObservable();

  public getTopQuestions(): Observable<QuestionList> {
    const data = this.http.get<QuestionList>(
      `${environment.urls.faqs.getTopQuestionList}`,
    );
    return data;
  }

  public getCategories(): Observable<FaqsMain> {
    return this.http
      .get<Faqs>(`${environment.urls.faqs.getCategories}${this.mainFaqsID}`)
      .pipe(
        map(value => value.faqsMain),
        this.mapValuesFiltered(FilterKeysItems.FAQ_CATEGORY_COLLECTION),
      );
  }

  public getSubCategoriesByCategoryName(
    categoryName: string,
  ): Observable<FaqsSubcategory> {
    return this.http
      .get<FaqsSubcategory>(
        `${environment.urls.faqs.getSubCategories}${categoryName}`,
      )
      .pipe(this.mapValuesFiltered(FilterKeysItems.FAQ_SUBCATEGORY_COLLECTION));
  }

  public getSubCategoriesByName(
    subCategoryName: string,
  ): Observable<FaqsSubcategory> {
    return this.http
      .get<FaqsSubcategory>(
        `${environment.urls.faqs.getSubCategoriesByName}${subCategoryName}/`,
      )
      .pipe(this.mapValuesFiltered(FilterKeysItems.FAQ_SUBCATEGORY_COLLECTION));
  }

  public getSubCategoriesFilteredByParentName(
    subCategoryName: string,
    parentName: string,
  ): Observable<FaqsSubcategory> {
    return this.http
      .get<FaqsSubcategory>(
        `${environment.urls.faqs.getSubCategories}${subCategoryName}/${parentName}`,
      )
      .pipe(this.mapValuesFiltered(FilterKeysItems.FAQ_SUBCATEGORY_COLLECTION));
  }

  public getQuestionListByName(
    previusName: string,
    parentName: string,
    questionListName: string,
  ): Observable<QuestionList> {
    return this.http
      .get<QuestionList>(
        `${
          environment.urls.faqs.getQuestionListByName
        }${questionListName}/${parentName}${
          previusName ? `/${previusName}` : ''
        }`,
      )
      .pipe(this.mapValuesFiltered(FilterKeysItems.FAQ_ITEM_COLLECTION));
  }

  public getQuestionByName(
    parentName: string,
    questionName: string,
  ): Observable<QuestionItem> {
    const data = this.http.get<QuestionItem>(
      `${environment.urls.faqs.getQuestionByName}${questionName}/${
        parentName ? parentName : ''
      }`,
    );
    return data;
  }

  public autocomplete(query: string): Observable<any> {
    let params = new HttpParams();
    params = params.append('q', query);

    return this.http.get(`${environment.urls.faqs.autocomplete}`, { params });
  }

  public getSearchData(): Observable<HeroSupport[]> {
    const hero = this.http.get<HeroSupport[]>(
      `${environment.urls.faqs.faqsHero}`,
    );
    return hero;
  }

  public getNavbar(): Observable<NX23NavbarFields> {
    const id = '6BYq8JxN4WXAAnXHzXq4eG';
    return this.http.get<NX23NavbarFields>(
      `${environment.urls.faqs.navbar}${id}`,
    );
  }

  searchFaqs(query: string): Observable<Question[]> {
    let params = new HttpParams();
    params = params.append('q', query);
    params = params.append(
      'userAccess',
      this.isAuthenticated ? userAccess.PRIVATE : userAccess.PUBLIC,
    );
    const searchFaqs = this.http.get<Question[]>(
      `${environment.urls.faqs.searchFaqs}`,
      { params },
    );
    return searchFaqs;
  }

  // TODO: aca tiene que ir el servicio post que guarda la respuesta del componente util
  postUtil(resp: any): Observable<boolean> {
    return of(resp);
  }

  getFooter(): Observable<any> {
    const footer = this.http
      .get<any>(`${environment.urls.faqs.footer}${this.footerContentfulID}`)
      .pipe();
    return footer;
  }

  private mapValuesFiltered<T extends { [key: string]: any }>(
    key: string,
  ): OperatorFunction<T, T> {
    return map((response: T) => {
      const items = this.filterItemsFromContentful(response[key].items);
      return {
        ...response,
        [key]: {
          items,
        },
      };
    });
  }

  private filterItemsFromContentful<T>(items: T[]): T[] {
    return items.filter((item: any) => {
      if (!item.publicPrivateModel || item.publicPrivateModel === null)
        return true;

      if (this.isAuthenticated)
        return !!item.publicPrivateModel?.includes(
          PUBLIC_PRIVATE_MODEL.USER_LOGGED_IN,
        );

      if (!this.isAuthenticated)
        return !!item.publicPrivateModel?.includes(
          PUBLIC_PRIVATE_MODEL.USER_NOT_LOGGED_IN,
        );

      return false;
    });
  }
}

enum FilterKeysItems {
  FAQ_CATEGORY_COLLECTION = 'faqsCategoryCollection',
  FAQ_SUBCATEGORY_COLLECTION = 'faqsSubCategoryCollection',
  FAQ_ITEM_COLLECTION = 'faqItemCollection',
}

enum userAccess {
  PUBLIC = 'public',
  PRIVATE = 'private',
}
